import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useActiveProgramCurrency, useActiveProgramId } from "./programs/program-scope";
import { apiErrorMessage } from "@/api/perkpoint/client";
import {
  InsightsApi,
  insightKeys,
  type ApiSegmentRules,
  type ApiSegmentsOverview,
} from "@/api/perkpoint/insights";
import { Plus, SlidersHorizontal, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

type BusinessType = "retail" | "fnb" | "hospitality" | "ecommerce";

type SegmentRules = {
  businessType: BusinessType;
  recentDays: number;
  lapsingDays: number;
  highFrequency: number;
  mediumFrequency: number;
  highTicket: number;
  mediumTicket: number;
};

type SegmentId =
  | "new"
  | "vip"
  | "loyal"
  | "promising"
  | "needs-attention"
  | "at-risk"
  | "lost"
  | "cant-lose";

type CustomSegment = {
  id: string;
  label: string;
  baseColor: string;
  stripeColor: string;
  recencyMaxDays: number;
  minFrequency: number;
  minTicket: number;
};

const CUSTOM_PALETTE = [
  { baseColor: "#f3e8ff", stripeColor: "rgba(168, 85, 247, 0.24)" },
  { baseColor: "#fce7f3", stripeColor: "rgba(236, 72, 153, 0.22)" },
  { baseColor: "#e0f2fe", stripeColor: "rgba(14, 165, 233, 0.24)" },
  { baseColor: "#fef3c7", stripeColor: "rgba(245, 158, 11, 0.28)" },
  { baseColor: "#ffe4e6", stripeColor: "rgba(244, 63, 94, 0.22)" },
  { baseColor: "#ecfccb", stripeColor: "rgba(132, 204, 22, 0.28)" },
];

const EMPTY_SEGMENT_DRAFT = {
  label: "",
  recencyMaxDays: 30,
  minFrequency: 1,
  minTicket: 500,
};

const BUSINESS_PRESETS: Record<
  BusinessType,
  Omit<SegmentRules, "businessType"> & { label: string; summary: string }
> = {
  retail: {
    label: "Retail",
    summary: "Monthly store visits, mid ticket.",
    recentDays: 30,
    lapsingDays: 90,
    highFrequency: 8,
    mediumFrequency: 3,
    highTicket: 2500,
    mediumTicket: 800,
  },
  fnb: {
    label: "Food & beverage",
    summary: "Short gaps between visits, lower ticket.",
    recentDays: 14,
    lapsingDays: 45,
    highFrequency: 12,
    mediumFrequency: 4,
    highTicket: 800,
    mediumTicket: 250,
  },
  hospitality: {
    label: "Hospitality",
    summary: "Longer gaps, higher ticket.",
    recentDays: 90,
    lapsingDays: 180,
    highFrequency: 4,
    mediumFrequency: 2,
    highTicket: 15000,
    mediumTicket: 5000,
  },
  ecommerce: {
    label: "Ecommerce",
    summary: "Online orders, mid recency.",
    recentDays: 21,
    lapsingDays: 60,
    highFrequency: 6,
    mediumFrequency: 2,
    highTicket: 4000,
    mediumTicket: 1200,
  },
};

const SEGMENT_META: {
  id: SegmentId;
  label: string;
  baseColor: string;
  stripeColor: string;
}[] = [
  {
    id: "new",
    label: "New",
    baseColor: "#e7f6ee",
    stripeColor: "rgba(16, 185, 129, 0.28)",
  },
  {
    id: "vip",
    label: "VIP",
    baseColor: "#e8eefb",
    stripeColor: "rgba(59, 130, 246, 0.22)",
  },
  {
    id: "loyal",
    label: "Loyal",
    baseColor: "#e5f7fb",
    stripeColor: "rgba(34, 211, 238, 0.32)",
  },
  {
    id: "promising",
    label: "Promising",
    baseColor: "#fdf0e6",
    stripeColor: "rgba(249, 115, 22, 0.22)",
  },
  {
    id: "needs-attention",
    label: "Needs Attention",
    baseColor: "#eeeaf8",
    stripeColor: "rgba(129, 140, 248, 0.28)",
  },
  {
    id: "at-risk",
    label: "At Risk",
    baseColor: "#fbf6d8",
    stripeColor: "rgba(234, 179, 8, 0.28)",
  },
  {
    id: "lost",
    label: "Lost",
    baseColor: "#fde8e8",
    stripeColor: "rgba(248, 113, 113, 0.28)",
  },
  {
    id: "cant-lose",
    label: "Can't Lose",
    baseColor: "#e5f8f6",
    stripeColor: "rgba(45, 212, 191, 0.32)",
  },
];

function defaultRules(): SegmentRules {
  return { businessType: "retail", ...stripPreset(BUSINESS_PRESETS.retail) };
}

function stripPreset(
  preset: (typeof BUSINESS_PRESETS)[BusinessType],
): Omit<SegmentRules, "businessType"> {
  return {
    recentDays: preset.recentDays,
    lapsingDays: preset.lapsingDays,
    highFrequency: preset.highFrequency,
    mediumFrequency: preset.mediumFrequency,
    highTicket: preset.highTicket,
    mediumTicket: preset.mediumTicket,
  };
}

function validateRules(rules: SegmentRules): string | null {
  if (!Number.isFinite(rules.recentDays) || rules.recentDays < 1) {
    return "Recent window must be at least 1 day.";
  }
  if (rules.lapsingDays <= rules.recentDays) {
    return "Lapsing window must be longer than the recent window.";
  }
  if (!Number.isFinite(rules.mediumFrequency) || rules.mediumFrequency < 1) {
    return "Medium frequency must be at least 1 visit.";
  }
  if (rules.highFrequency <= rules.mediumFrequency) {
    return "High frequency must be above medium frequency.";
  }
  if (!Number.isFinite(rules.mediumTicket) || rules.mediumTicket < 1) {
    return "Medium ticket value must be at least 1.";
  }
  if (rules.highTicket <= rules.mediumTicket) {
    return "High ticket value must be above the medium ticket.";
  }
  return null;
}

function validateCustomSegment(
  draft: typeof EMPTY_SEGMENT_DRAFT,
  existing: CustomSegment[],
): string | null {
  const label = draft.label.trim();
  if (!label) return "Enter a segment name.";
  if (label.length > 40) return "Segment name must be 40 characters or fewer.";
  const taken = [...SEGMENT_META.map((segment) => segment.label), ...existing.map((segment) => segment.label)];
  if (taken.some((name) => name.toLowerCase() === label.toLowerCase())) {
    return "A segment with this name already exists.";
  }
  if (!Number.isFinite(draft.recencyMaxDays) || draft.recencyMaxDays < 1) {
    return "Recency must be at least 1 day.";
  }
  if (!Number.isFinite(draft.minFrequency) || draft.minFrequency < 1) {
    return "Frequency must be at least 1 visit.";
  }
  if (!Number.isFinite(draft.minTicket) || draft.minTicket < 1) {
    return "Ticket value must be at least 1.";
  }
  return null;
}

function formatTicket(value: number, currency: string) {
  const code = (currency || "").trim().toUpperCase();
  const safe = /^[A-Z]{3}$/.test(code) ? code : "AED";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: safe,
    maximumFractionDigits: 0,
  }).format(value);
}

function NumberField({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        min={1}
        value={Number.isFinite(value) ? value : ""}
        onChange={(event) => onChange(Number(event.target.value))}
      />
      <p className="text-xs text-slate-500">{hint}</p>
    </div>
  );
}

function rulesFromApi(rules: ApiSegmentRules): SegmentRules {
  return {
    businessType: (rules.business_type in BUSINESS_PRESETS ? rules.business_type : "retail") as BusinessType,
    recentDays: rules.recent_days,
    lapsingDays: rules.lapsing_days,
    highFrequency: rules.high_frequency,
    mediumFrequency: rules.medium_frequency,
    highTicket: rules.high_ticket,
    mediumTicket: rules.medium_ticket,
  };
}

export default function MemberSegments() {
  const queryClient = useQueryClient();
  const { programId } = useActiveProgramId();
  const { currency } = useActiveProgramCurrency();
  const overviewQuery = useQuery({
    queryKey: insightKeys.segments(programId ?? ""),
    queryFn: () => InsightsApi.segments(programId!),
    enabled: Boolean(programId),
  });
  const overview = overviewQuery.data;
  const rules: SegmentRules = overview ? rulesFromApi(overview.rules) : defaultRules();
  const customRules = overview?.rules.business_type === "custom";
  const customSegments: CustomSegment[] = useMemo(
    () =>
      (overview?.custom_segments ?? []).map((segment, index) => ({
        id: segment.id,
        label: segment.label,
        ...CUSTOM_PALETTE[index % CUSTOM_PALETTE.length],
        recencyMaxDays: segment.recency_max_days ?? 0,
        minFrequency: segment.min_frequency ?? 0,
        minTicket: segment.min_ticket ?? 0,
      })),
    [overview],
  );
  const [draft, setDraft] = useState<SegmentRules>(defaultRules);
  const [configOpen, setConfigOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [segmentDraft, setSegmentDraft] = useState(EMPTY_SEGMENT_DRAFT);
  const [segmentError, setSegmentError] = useState<string | null>(null);

  const cards = useMemo(() => {
    const builtIn = SEGMENT_META.map((segment) => {
      const api = overview?.built_in.find((row) => row.key === segment.id);
      return {
        ...segment,
        custom: false as const,
        members: api?.members ?? 0,
        percent: api?.share_pct ?? 0,
      };
    });
    const custom = customSegments.map((segment) => {
      const api = overview?.custom_segments.find((row) => row.id === segment.id);
      return {
        ...segment,
        custom: true as const,
        members: api?.members ?? 0,
        percent: api?.share_pct ?? 0,
      };
    });
    return [...builtIn, ...custom];
  }, [overview, customSegments]);

  const totalMembers = overview?.total_members ?? 0;
  const preset = customRules
    ? { label: "Custom", summary: "Your own thresholds." }
    : BUSINESS_PRESETS[rules.businessType];

  const onSaved = (next: ApiSegmentsOverview) => {
    queryClient.setQueryData(insightKeys.segments(programId ?? ""), next);
    queryClient.invalidateQueries({ queryKey: insightKeys.all });
    queryClient.invalidateQueries({ queryKey: ["perkpoint", "members"] });
  };
  const rulesMutation = useMutation({
    mutationFn: (next: SegmentRules) => InsightsApi.saveSegmentRules(programId!, next),
    onSuccess: (next) => {
      onSaved(next);
      setConfigOpen(false);
      toast.success("Segment rules saved. Members were re-sorted.");
    },
    onError: (err) => setError(apiErrorMessage(err, "Could not save the rules")),
  });
  const addMutation = useMutation({
    mutationFn: () =>
      InsightsApi.addCustomSegment(programId!, {
        label: segmentDraft.label.trim(),
        recencyMaxDays: segmentDraft.recencyMaxDays,
        minFrequency: segmentDraft.minFrequency,
        minTicket: segmentDraft.minTicket,
      }),
    onSuccess: (next) => {
      onSaved(next);
      setAddOpen(false);
      toast.success(`${segmentDraft.label.trim()} added`);
    },
    onError: (err) => setSegmentError(apiErrorMessage(err, "Could not add the segment")),
  });
  const removeMutation = useMutation({
    mutationFn: (id: string) => InsightsApi.removeCustomSegment(programId!, id),
    onSuccess: (next) => {
      onSaved(next);
      toast.success("Segment removed");
    },
    onError: (err) => toast.error(apiErrorMessage(err, "Could not remove the segment")),
  });

  const openConfig = () => {
    setDraft(rules);
    setError(null);
    setConfigOpen(true);
  };

  const applyBusiness = (businessType: BusinessType) => {
    setDraft({
      businessType,
      ...stripPreset(BUSINESS_PRESETS[businessType]),
    });
    setError(null);
  };

  const applyRules = () => {
    const message = validateRules(draft);
    if (message) {
      setError(message);
      return;
    }
    if (!programId) return;
    rulesMutation.mutate(draft);
  };

  const openAddSegment = () => {
    setSegmentDraft(EMPTY_SEGMENT_DRAFT);
    setSegmentError(null);
    setAddOpen(true);
  };

  const addSegment = () => {
    const message = validateCustomSegment(segmentDraft, customSegments);
    if (message) {
      setSegmentError(message);
      return;
    }
    if (!programId) return;
    addMutation.mutate();
  };

  const removeSegment = (id: string) => {
    if (programId) removeMutation.mutate(id);
  };

  return (
    <div className="mx-auto min-w-0 max-w-6xl space-y-6 pb-4">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-[1.75rem] font-semibold tracking-tight text-slate-950 sm:text-4xl">
            Member Segmentation
          </h1>
          <p className="mt-2 max-w-xl text-sm text-slate-500 sm:text-base">
            Distribution of members across lifecycle segments.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            type="button"
            className="h-11 rounded-full bg-[#1B7A45] text-white hover:bg-[#16653A]"
            onClick={openAddSegment}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add segment
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 rounded-full bg-white"
            onClick={openConfig}
          >
            <SlidersHorizontal className="mr-2 h-4 w-4" />
            Configure segments
          </Button>
        </div>
      </header>

      <div className="flex flex-wrap gap-2 text-sm text-slate-600">
        <span className="rounded-full bg-white px-3 py-1 ring-1 ring-black/[0.06]">
          {preset.label}
        </span>
        <span className="rounded-full bg-white px-3 py-1 ring-1 ring-black/[0.06]">
          Recent ≤ {rules.recentDays} days
        </span>
        <span className="rounded-full bg-white px-3 py-1 ring-1 ring-black/[0.06]">
          Lapsing ≤ {rules.lapsingDays} days
        </span>
        <span className="rounded-full bg-white px-3 py-1 ring-1 ring-black/[0.06]">
          High frequency ≥ {rules.highFrequency}
        </span>
        <span className="rounded-full bg-white px-3 py-1 ring-1 ring-black/[0.06]">
          High ticket ≥ {formatTicket(rules.highTicket, currency)}
        </span>
      </div>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4 xl:gap-5">
        {cards.map((segment) => (
          <article
            key={segment.id}
            className="min-h-[168px] rounded-2xl p-5 sm:p-6"
            style={{
              backgroundColor: segment.baseColor,
              backgroundImage: `repeating-linear-gradient(135deg, ${segment.stripeColor} 0, ${segment.stripeColor} 2px, transparent 2px, transparent 7px)`,
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <p className="text-sm font-medium text-slate-700">{segment.label}</p>
              {segment.custom ? (
                <button
                  type="button"
                  className="rounded-full p-1 text-slate-500 hover:bg-white/70 hover:text-slate-900"
                  aria-label={`Remove ${segment.label}`}
                  onClick={() => removeSegment(segment.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              ) : null}
            </div>
            <p className="mt-8 text-4xl font-semibold tracking-tight text-slate-950 sm:text-5xl">
              {segment.percent}
              <span className="ml-1 align-top text-2xl">%</span>
            </p>
            <p className="mt-2 text-sm font-medium text-slate-800">
              {segment.members}{" "}
              <span className="font-normal text-slate-700">
                {segment.members === 1 ? "member" : "members"}
              </span>
            </p>
            {segment.custom ? (
              <p className="mt-3 text-xs text-slate-600">
                Last visit ≤ {segment.recencyMaxDays} days · ≥ {segment.minFrequency}{" "}
                visits · ticket ≥ {formatTicket(segment.minTicket, currency)}
              </p>
            ) : null}
          </article>
        ))}
      </section>

      <p className="text-sm text-slate-500">
        {!overview
          ? overviewQuery.isError
            ? apiErrorMessage(overviewQuery.error, "Could not load segments")
            : "Loading segments…"
          : null}
        {overview ? `${totalMembers} members classified with` : ""} {preset.label.toLowerCase()}{" "}
        rules
        {customSegments.length > 0
          ? ` and ${customSegments.length} custom ${customSegments.length === 1 ? "segment" : "segments"}`
          : ""}
        . Custom segments are matched first. Shares are of this member base.
      </p>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Add customer segment</DialogTitle>
            <DialogDescription>
              Name the segment and set who belongs in it. Members who match are
              counted here before the standard lifecycle segments.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="segment-name">Segment name</Label>
              <Input
                id="segment-name"
                value={segmentDraft.label}
                maxLength={40}
                placeholder="Weekend regulars"
                onChange={(event) =>
                  setSegmentDraft((current) => ({
                    ...current,
                    label: event.target.value,
                  }))
                }
              />
            </div>
            <NumberField
              id="segment-recency"
              label="Recency (days)"
              hint="Last visit must be within this many days."
              value={segmentDraft.recencyMaxDays}
              onChange={(recencyMaxDays) =>
                setSegmentDraft((current) => ({ ...current, recencyMaxDays }))
              }
            />
            <NumberField
              id="segment-frequency"
              label="Frequency (visits)"
              hint="Minimum visits in the period."
              value={segmentDraft.minFrequency}
              onChange={(minFrequency) =>
                setSegmentDraft((current) => ({ ...current, minFrequency }))
              }
            />
            <NumberField
              id="segment-ticket"
              label={`Ticket value (${currency || "program currency"})`}
              hint="Minimum average order value."
              value={segmentDraft.minTicket}
              onChange={(minTicket) =>
                setSegmentDraft((current) => ({ ...current, minTicket }))
              }
            />
            {segmentError ? (
              <p className="text-sm text-red-600" role="alert">
                {segmentError}
              </p>
            ) : null}
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="bg-[#1B7A45] text-white hover:bg-[#16653A]"
              onClick={addSegment}
            >
              Add segment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={configOpen} onOpenChange={setConfigOpen}>
        <SheetContent className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Configure segmentation</SheetTitle>
            <SheetDescription>
              Set the business, then tune recency, frequency, and ticket value.
              The chart updates from these cutoffs.
            </SheetDescription>
          </SheetHeader>

          <div className="mt-6 space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="business-type">Business</Label>
              <Select
                value={draft.businessType}
                onValueChange={(value) => applyBusiness(value as BusinessType)}
              >
                <SelectTrigger id="business-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(BUSINESS_PRESETS) as BusinessType[]).map(
                    (key) => (
                      <SelectItem key={key} value={key}>
                        {BUSINESS_PRESETS[key].label}
                      </SelectItem>
                    ),
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500">
                {BUSINESS_PRESETS[draft.businessType].summary} Choosing a
                business fills the fields below. You can still edit them.
              </p>
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-900">Recency</p>
              <NumberField
                id="recent-days"
                label="Recent window (days)"
                hint="Last visit inside this window counts as recent."
                value={draft.recentDays}
                onChange={(recentDays) =>
                  setDraft((current) => ({ ...current, recentDays }))
                }
              />
              <NumberField
                id="lapsing-days"
                label="Lapsing window (days)"
                hint="Past this window, the member is inactive."
                value={draft.lapsingDays}
                onChange={(lapsingDays) =>
                  setDraft((current) => ({ ...current, lapsingDays }))
                }
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-900">Frequency</p>
              <NumberField
                id="medium-frequency"
                label="Medium frequency (visits)"
                hint="Visits in the period needed to leave the New segment."
                value={draft.mediumFrequency}
                onChange={(mediumFrequency) =>
                  setDraft((current) => ({ ...current, mediumFrequency }))
                }
              />
              <NumberField
                id="high-frequency"
                label="High frequency (visits)"
                hint="Visits needed for Loyal, VIP, and Can't Lose."
                value={draft.highFrequency}
                onChange={(highFrequency) =>
                  setDraft((current) => ({ ...current, highFrequency }))
                }
              />
            </div>

            <div className="space-y-3">
              <p className="text-sm font-semibold text-slate-900">
                Ticket value
              </p>
              <NumberField
                id="medium-ticket"
                label={`Medium ticket (${currency || "program currency"})`}
                hint="Average order below this, once inactive, is Lost."
                value={draft.mediumTicket}
                onChange={(mediumTicket) =>
                  setDraft((current) => ({ ...current, mediumTicket }))
                }
              />
              <NumberField
                id="high-ticket"
                label={`High ticket (${currency || "program currency"})`}
                hint="Average order needed for VIP and Can't Lose."
                value={draft.highTicket}
                onChange={(highTicket) =>
                  setDraft((current) => ({ ...current, highTicket }))
                }
              />
            </div>

            {error ? (
              <p className="text-sm text-red-600" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <SheetFooter className="mt-6 gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => applyBusiness(draft.businessType)}
            >
              Reset preset
            </Button>
            <Button
              type="button"
              className="bg-[#1B7A45] text-white hover:bg-[#16653A]"
              onClick={applyRules}
            >
              Apply
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}
